import { Card, CardContent } from '@ui/card.tsx';
import { MenuItem } from '@ui/menu-item.tsx';
import { LinkProtected } from '@r/components/link-protected/link-protected.tsx';
import { useTranslation } from 'react-i18next';
import { Link2, Facebook, Linkedin, Youtube, Twitter, Instagram } from 'lucide-react';

import { useGetConfigByType } from '@r/hooks/use-workspace-config.ts';

function toAbsoluteUrl(url: string): string {
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return `https://${url}`;
  }
  return url;
}

export function Footer() {
  const { t } = useTranslation();

  const workspaceTitle = useGetConfigByType('core', 'title')?.[0];

  const customLink = useGetConfigByType('link', 'custom-link')?.[0];
  const facebookLink = useGetConfigByType('link', 'facebook-link')?.[0];
  const linkedinLink = useGetConfigByType('link', 'linkedin-link')?.[0];
  const youtubeLink = useGetConfigByType('link', 'youtube-link')?.[0];
  const xLink = useGetConfigByType('link', 'x-link')?.[0];
  const instagramLink = useGetConfigByType('link', 'instagram-link')?.[0];

  const hasSocialLinks = facebookLink || linkedinLink || youtubeLink || xLink || instagramLink;

  const socialLinks = [
    { href: facebookLink, icon: Facebook },
    { href: linkedinLink, icon: Linkedin },
    { href: youtubeLink, icon: Youtube },
    { href: xLink, icon: Twitter },
    { href: instagramLink, icon: Instagram }
  ] as const;

  return (
    <footer className="app-shell sm:pt-7 sm:px-7 sm:pb-5">
      <Card className="text-base rounded-none border-0 bg-footer font-inter text-primary-foreground sm:rounded-3xl">
        <CardContent className="p-5">
          <div className="p-2 mb-2 text-4xl font-medium font-author-complete">{workspaceTitle}</div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex flex-col">
              <div className="flex flex-col mb-8 w-full md:flex-row lg:space-x-4">
                <LinkProtected to="/">
                  <MenuItem
                    onClick={() => {}}
                    className="hover:bg-background hover:text-foreground"
                  >
                    {t('footer.home')}
                  </MenuItem>
                </LinkProtected>

                <LinkProtected to="/program">
                  <MenuItem
                    onClick={() => {}}
                    className="hover:bg-background hover:text-foreground"
                  >
                    {t('footer.program')}
                  </MenuItem>
                </LinkProtected>

                <LinkProtected to="/performance">
                  <MenuItem
                    onClick={() => {}}
                    className="hover:bg-background hover:text-foreground"
                  >
                    {t('footer.performances')}
                  </MenuItem>
                </LinkProtected>

                <LinkProtected to="/shops">
                  <MenuItem
                    onClick={() => {}}
                    className="hover:bg-background hover:text-foreground"
                  >
                    {t('footer.shops')}
                  </MenuItem>
                </LinkProtected>

                <LinkProtected to="/account">
                  <MenuItem
                    onClick={() => {}}
                    className="hover:bg-background hover:text-foreground"
                  >
                    {t('footer.account')}
                  </MenuItem>
                </LinkProtected>
              </div>

              <div className="flex flex-col flex-wrap w-full sm:flex-row lg:space-x-4">
                <LinkProtected to="/legal-notice" target={'_blank'}>
                  <MenuItem
                    onClick={() => {}}
                    className="hover:bg-background hover:text-foreground"
                  >
                    {t('footer.legal.notice')}
                  </MenuItem>
                </LinkProtected>

                <LinkProtected to="/privacy-policy" target={'_blank'}>
                  <MenuItem
                    onClick={() => {}}
                    className="hover:bg-background hover:text-foreground"
                  >
                    {t('footer.privacy.policy')}
                  </MenuItem>
                </LinkProtected>

                <LinkProtected to="/terms-and-conditions" target={'_blank'}>
                  <MenuItem
                    onClick={() => {}}
                    className="hover:bg-background hover:text-foreground"
                  >
                    {t('footer.terms.and.conditions')}
                  </MenuItem>
                </LinkProtected>
              </div>
            </div>

            {(customLink || hasSocialLinks) && (
              <div className="flex flex-col items-end gap-4 ml-auto">
                {customLink && (
                  <a
                    href={toAbsoluteUrl(customLink)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm hover:opacity-70 transition-opacity"
                  >
                    <Link2 className="w-4 h-4 shrink-0" />
                    <span>{t('personalization.external.links.custom.link.display')}</span>
                  </a>
                )}

                {hasSocialLinks && (
                  <div className="flex items-center gap-3">
                    {socialLinks.map(
                      ({ href, icon: Icon }) =>
                        href && (
                          <a
                            key={href}
                            href={toAbsoluteUrl(href)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:opacity-70 transition-opacity"
                          >
                            <Icon className="w-5 h-5" />
                          </a>
                        )
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </footer>
  );
}
